import {
  CarOutlined, CheckCircleOutlined, ClockCircleOutlined, PlusOutlined, SaveOutlined, SearchOutlined, UserOutlined
} from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin, Tag, Typography
} from "antd";
import dayjs from "dayjs";
import { useDeferredValue, useEffect, useState } from "react";
import { Controller, useForm, type Control } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import type { CustomerSummary, VehicleSummary } from "@autoflow/types";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import {
  capitalizeNameInput, formatGroupedInput, formatPhone, formatPhoneInput, isPhoneSearchTerm, normalizeVinInput, parseGroupedInput
} from "../../shared/utils/formatters";
import { customersApi } from "../customers/customers-api";
import { customerName } from "../customers/customer-types";
import { ScanButton } from "../scan/ScanButton";
import { FieldScanButton } from "../scan/FieldScanButton";
import { vehiclesApi } from "../vehicles/vehicles-api";
import { repairOrdersApi } from "./repair-orders-api";
import { useTimedNoResults } from "../../shared/hooks/useTimedNoResults";

const fastRoSchema = z.object({
  customerId: z.string().optional(),
  vehicleId: z.string().optional(),
  salutation: z.enum(["MR", "MS"]).optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  alternatePhone: z.string().optional(),
  email: z.string().email("Valid email is required").optional().or(z.literal("")),
  vin: z.string().optional(),
  licensePlate: z.string().optional(),
  year: z.number().int().min(1886).max(2100).optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  mileageIn: z.number().int().min(0).optional(),
  promisedAt: z.string().optional(),
  customerConcern: z.string().min(1, "Customer complaint is required")
}).superRefine((value, context) => {
  if (!value.customerId) {
    if (!value.firstName?.trim()) context.addIssue({ code: z.ZodIssueCode.custom, path: ["firstName"], message: "First name is required" });
    if (!value.lastName?.trim()) context.addIssue({ code: z.ZodIssueCode.custom, path: ["lastName"], message: "Last name is required" });
    const phoneDigits = value.phone?.replace(/\D/g, "") ?? "";
    if (!(phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith("1")))) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: "10-digit phone is required" });
    }
    const alternatePhoneDigits = value.alternatePhone?.replace(/\D/g, "") ?? "";
    if (alternatePhoneDigits && !(alternatePhoneDigits.length === 10 || (alternatePhoneDigits.length === 11 && alternatePhoneDigits.startsWith("1")))) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["alternatePhone"], message: "10-digit phone is required" });
    }
  }
  if (!value.vehicleId) {
    if (!value.vin || !/^[A-HJ-NPR-Z0-9]{17}$/.test(value.vin)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["vin"], message: "Valid 17-character VIN is required" });
    if (!value.year) context.addIssue({ code: z.ZodIssueCode.custom, path: ["year"], message: "Year is required" });
    if (!value.make?.trim()) context.addIssue({ code: z.ZodIssueCode.custom, path: ["make"], message: "Make is required" });
    if (!value.model?.trim()) context.addIssue({ code: z.ZodIssueCode.custom, path: ["model"], message: "Model is required" });
  }
});

type FastRoValues = z.infer<typeof fastRoSchema>;
type CustomerChoice = CustomerSummary;
type VehicleChoice = VehicleSummary & { customer: CustomerSummary };
const FAST_RO_DRAFT_KEY = "autoflow.fast-ro-draft.v1";

export function NewRepairOrderPage() {
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const lookup = params.get("lookup")?.trim() ?? "";
  const inferredLookup = inferLookup(lookup);
  const hasEntryContext = Boolean(lookup || params.get("customerId") || params.get("vehicleId") || params.get("vin"));
  const [restoredDraft] = useState(() => hasEntryContext ? undefined : loadFastRoDraft());
  const [search, setSearch] = useState(lookup);
  const [savedAt, setSavedAt] = useState(restoredDraft?.savedAt);
  const deferredSearch = useDeferredValue(search.trim());
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerChoice>();
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleChoice>();
  const scanned = params.get("source") === "scan";

  const { control, getValues, handleSubmit, setValue, watch, formState: { errors } } = useForm<FastRoValues>({
    resolver: zodResolver(fastRoSchema),
    defaultValues: {
      ...restoredDraft?.values,
      customerId: params.get("customerId") ?? restoredDraft?.values.customerId ?? "",
      vehicleId: params.get("vehicleId") ?? restoredDraft?.values.vehicleId ?? "",
      salutation: restoredDraft?.values.salutation,
      firstName: inferredLookup.firstName ?? restoredDraft?.values.firstName ?? "",
      lastName: inferredLookup.lastName ?? restoredDraft?.values.lastName ?? "",
      phone: inferredLookup.phone ?? restoredDraft?.values.phone ?? "",
      alternatePhone: restoredDraft?.values.alternatePhone ?? "",
      email: restoredDraft?.values.email ?? "",
      vin: normalizeVinInput(params.get("vin") ?? inferredLookup.vin ?? restoredDraft?.values.vin ?? ""),
      licensePlate: inferredLookup.licensePlate ?? restoredDraft?.values.licensePlate ?? "",
      year: restoredDraft?.values.year ?? new Date().getFullYear(),
      customerConcern: restoredDraft?.values.customerConcern ?? ""
    }
  });
  const customerId = watch("customerId");
  const vehicleId = watch("vehicleId");
  const vin = watch("vin");

  const initialCustomer = useQuery({
    queryKey: ["fast-ro-customer", customerId],
    queryFn: () => customersApi.get(customerId!),
    enabled: Boolean(customerId && !selectedCustomer)
  });
  const initialVehicle = useQuery({
    queryKey: ["fast-ro-vehicle", vehicleId],
    queryFn: () => vehiclesApi.get(vehicleId!),
    enabled: Boolean(vehicleId && !selectedVehicle)
  });
  const customerVehicles = useQuery({
    queryKey: ["fast-ro-customer-vehicles", customerId],
    queryFn: () => vehiclesApi.list("", customerId, 1),
    enabled: Boolean(customerId && !vehicleId)
  });
  const searchResults = useQuery({
    queryKey: ["fast-ro-search", deferredSearch],
    enabled: deferredSearch.length >= 2,
    queryFn: () => {
      const term = isPhoneSearchTerm(deferredSearch) ? formatPhoneInput(deferredSearch) : deferredSearch;
      return repairOrdersApi.arrivalSearch(term);
    }
  });
  const vinDecode = useQuery({
    queryKey: ["fast-ro-vin-decode", vin],
    queryFn: () => vehiclesApi.decodeVin(vin!),
    enabled: !vehicleId && /^[A-HJ-NPR-Z0-9]{17}$/.test(vin ?? ""),
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1
  });
  const existingVinVehicle = useQuery({
    queryKey: ["fast-ro-existing-vin", vin],
    queryFn: () => vehiclesApi.list(vin!, undefined, 1),
    enabled: !vehicleId && /^[A-HJ-NPR-Z0-9]{17}$/.test(vin ?? ""),
    staleTime: 30_000
  });
  const selectedVin = initialVehicle.data?.vin ?? selectedVehicle?.vin ?? vin;
  const activeRepairEvent = useQuery({
    queryKey: ["fast-ro-active-event", selectedVin],
    queryFn: () => repairOrdersApi.arrivalSearch(selectedVin!),
    enabled: Boolean(/^[A-HJ-NPR-Z0-9]{17}$/.test(selectedVin ?? "")),
    staleTime: 15_000
  });
  const hasSearchResults = Boolean(searchResults.data && (
    searchResults.data.repairEvents.length || searchResults.data.vehicles.length || searchResults.data.customers.length
  ));
  const noSearchResultsDismissed = useTimedNoResults({
    query: deferredSearch,
    enabled: deferredSearch.length >= 2,
    loading: searchResults.isFetching,
    ready: Boolean(searchResults.data),
    hasResults: hasSearchResults
  });

  useEffect(() => {
    if (initialCustomer.data) setSelectedCustomer(initialCustomer.data);
  }, [initialCustomer.data]);
  useEffect(() => {
    if (initialVehicle.data) {
      setSelectedVehicle(initialVehicle.data);
      setValue("customerId", initialVehicle.data.customerId);
      setSelectedCustomer(initialVehicle.data.customer);
    }
  }, [initialVehicle.data, setValue]);
  useEffect(() => {
    const vin = params.get("vin");
    if (vin) setValue("vin", normalizeVinInput(vin));
  }, [params, setValue]);
  useEffect(() => {
    if (!vinDecode.data || vehicleId) return;
    if (vinDecode.data.year) setValue("year", vinDecode.data.year);
    if (vinDecode.data.make) setValue("make", vehicleDisplayCase(vinDecode.data.make));
    if (vinDecode.data.model) setValue("model", vehicleDisplayCase(vinDecode.data.model));
  }, [setValue, vehicleId, vinDecode.data]);
  useEffect(() => {
    if (vehicleId) return;
    const existing = existingVinVehicle.data?.items.find(item => item.vin === vin);
    if (!existing) return;
    setSelectedVehicle(existing);
    setSelectedCustomer(existing.customer);
    setValue("customerId", existing.customerId);
    setValue("vehicleId", existing.id);
    setValue("mileageIn", existing.mileage ?? undefined);
  }, [existingVinVehicle.data, setValue, vehicleId, vin]);
  const saveDraft = () => {
    const nextSavedAt = new Date().toISOString();
    localStorage.setItem(FAST_RO_DRAFT_KEY, JSON.stringify({ values: getValues(), savedAt: nextSavedAt }));
    setSavedAt(nextSavedAt);
  };

  const selectCustomer = (customer: CustomerChoice) => {
    setSelectedCustomer(customer);
    setSelectedVehicle(undefined);
    setValue("customerId", customer.id);
    setValue("vehicleId", "");
    setSearch("");
  };
  const selectVehicle = (vehicle: VehicleChoice) => {
    setSelectedVehicle(vehicle);
    setSelectedCustomer(vehicle.customer);
    setValue("customerId", vehicle.customerId);
    setValue("vehicleId", vehicle.id);
    setValue("mileageIn", vehicle.mileage ?? undefined);
    setSearch("");
  };

  const create = useMutation({
    mutationFn: async (values: FastRoValues) => {
      return repairOrdersApi.fastCheckIn({
        customerId: values.customerId || undefined,
        vehicleId: values.vehicleId || undefined,
        customer: values.customerId ? undefined : {
          salutation: values.salutation,
          firstName: values.firstName!.trim(),
          lastName: values.lastName!.trim(),
          phone: formatPhoneInput(values.phone!),
          alternatePhone: values.alternatePhone ? formatPhoneInput(values.alternatePhone) : undefined,
          email: values.email?.trim().toLowerCase() || undefined
        },
        vehicle: values.vehicleId ? undefined : {
          vin: normalizeVinInput(values.vin!),
          licensePlate: values.licensePlate?.trim().toUpperCase() || undefined,
          year: values.year!,
          make: values.make!.trim(),
          model: values.model!.trim()
        },
        mileageIn: values.mileageIn,
        promisedAt: values.promisedAt,
        customerConcern: values.customerConcern.trim()
      });
    },
    onSuccess: repairOrder => {
      localStorage.removeItem(FAST_RO_DRAFT_KEY);
      navigate(repairOrder.attention.workspacePath, { replace: true });
    }
  });

  const effectiveCustomer = selectedCustomer ?? initialCustomer.data;
  const effectiveVehicle = selectedVehicle ?? initialVehicle.data;
  const existingActiveEvent = activeRepairEvent.data?.repairEvents.find(item =>
    item.vehicle.vin === selectedVin
  );
  return <section className="fast-ro-page">
    <div className="page-title fast-ro-title">
      <div>
        <Typography.Text type="secondary">{text.frontDesk}</Typography.Text>
        <Typography.Title level={2}>{text.fastCheckIn}</Typography.Title>
        <Typography.Paragraph type="secondary">{text.fastCheckInHint}</Typography.Paragraph>
      </div>
      <Space><Tag color={scanned ? "green" : "blue"}>{scanned ? text.scanStarted : text.target60Seconds}</Tag><ScanButton /></Space>
    </div>

    {create.isError && <Alert showIcon type="error" message={(create.error as Error).message} className="fast-ro-error" />}
    {restoredDraft && <Alert showIcon type="info" message={text.draftRestored} className="fast-ro-error" closable />}
    {existingActiveEvent && <Alert
      showIcon
      type="warning"
      className="fast-ro-error"
      message={`${text.activeRepairEventExists}: ${existingActiveEvent.roNumber}`}
      description={text.continueActiveRepairHint}
      action={<Button type="primary" onClick={() => navigate(existingActiveEvent.attention.workspacePath)}>
        {text.continueActiveRepair}
      </Button>}
    />}

    <Card className="fast-ro-search-card">
      <Input
        size="large"
        autoFocus
        allowClear
        prefix={<SearchOutlined />}
        placeholder={text.fastRoSearchPlaceholder}
        value={search}
        onChange={event => setSearch(event.target.value.toUpperCase())}
      />
      {deferredSearch.length >= 2 && !noSearchResultsDismissed && <div className="fast-ro-search-results">
        {searchResults.isFetching ? <Spin size="small" /> : <>
          {(searchResults.data?.repairEvents ?? []).map(repairEvent => <button
            type="button"
            key={repairEvent.id}
            onClick={() => navigate(repairEvent.attention.workspacePath)}
          >
            <ClockCircleOutlined /><span><b>{repairEvent.roNumber} · {text.continueActiveRepair}</b><small>
              {repairEvent.vehicle.year} {repairEvent.vehicle.make} {repairEvent.vehicle.model} · {customerName(repairEvent.customer)}
            </small></span>
          </button>)}
          {(searchResults.data?.vehicles ?? []).map(vehicle => <button type="button" key={vehicle.id} onClick={() => selectVehicle(vehicle)}>
            <CarOutlined /><span><b>{vehicle.year} {vehicle.make} {vehicle.model}</b><small>{vehicle.vin} · {customerName(vehicle.customer)}</small></span>
          </button>)}
          {(searchResults.data?.customers ?? []).map(customer => <button type="button" key={customer.id} onClick={() => selectCustomer(customer)}>
            <UserOutlined /><span><b>{customerName(customer)}</b><small>{formatPhone(customer.phone)} · {customer._count.vehicles} {text.vehicles}</small></span>
          </button>)}
          {!searchResults.data?.repairEvents.length && !searchResults.data?.vehicles.length && !searchResults.data?.customers.length && <div className="fast-ro-no-result"><b>{text.noSearchResults}</b><span>{text.quickCreateBelow}</span></div>}
        </>}
      </div>}
    </Card>

    <form onSubmit={handleSubmit(values => create.mutate(values))}>
      <Row gutter={[18, 18]} className="fast-ro-grid">
        <Col xs={24} lg={12}>
          <Card title={<Space><UserOutlined />{text.customer}</Space>} extra={effectiveCustomer && <Tag icon={<CheckCircleOutlined />} color="success">{text.selected}</Tag>}>
            {effectiveCustomer ? <SelectedEntity
              title={customerName(effectiveCustomer)}
              lines={[
                `${text.phone}: ${formatPhone(effectiveCustomer.phone)}`,
                `${text.alternatePhone}: ${formatPhone(effectiveCustomer.alternatePhone)}`,
                `${text.email}: ${effectiveCustomer.email || "—"}`
              ]}
              changeLabel={text.change}
              onChange={() => { setSelectedCustomer(undefined); setSelectedVehicle(undefined); setValue("customerId", ""); setValue("vehicleId", ""); }}
            /> : <Row gutter={12}>
              <Col xs={12} md={9}><FastTextField control={control} name="firstName" label={text.firstName} error={errors.firstName?.message} nameCase /></Col>
              <Col xs={12} md={9}><FastTextField control={control} name="lastName" label={text.lastName} error={errors.lastName?.message} nameCase /></Col>
              <Col xs={24} md={6}><Controller name="salutation" control={control} render={({ field }) => <Form.Item label={text.salutation}><Select {...field} allowClear options={[{ label: text.mr, value: "MR" }, { label: text.ms, value: "MS" }]} /></Form.Item>} /></Col>
              <Col xs={24}><Controller name="phone" control={control} render={({ field }) => <Form.Item label={text.phone} validateStatus={errors.phone ? "error" : undefined} help={errors.phone?.message}><Input {...field} inputMode="tel" value={formatPhoneInput(field.value ?? "")} onChange={event => field.onChange(formatPhoneInput(event.target.value))} /></Form.Item>} /></Col>
              <Col xs={24} md={12}><Controller name="alternatePhone" control={control} render={({ field }) => <Form.Item label={text.alternatePhone} validateStatus={errors.alternatePhone ? "error" : undefined} help={errors.alternatePhone?.message}><Input {...field} inputMode="tel" value={formatPhoneInput(field.value ?? "")} onChange={event => field.onChange(formatPhoneInput(event.target.value))} /></Form.Item>} /></Col>
              <Col xs={24} md={12}><Controller name="email" control={control} render={({ field }) => <Form.Item label={text.email} validateStatus={errors.email ? "error" : undefined} help={errors.email?.message}><Input {...field} inputMode="email" autoComplete="email" /></Form.Item>} /></Col>
            </Row>}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<Space><CarOutlined />{text.vehicle}</Space>} extra={effectiveVehicle && <Tag icon={<CheckCircleOutlined />} color="success">{text.selected}</Tag>}>
            {effectiveVehicle ? <>
              <SelectedEntity
                title={`${effectiveVehicle.year} ${effectiveVehicle.make} ${effectiveVehicle.model}`}
                lines={[effectiveVehicle.vin, effectiveVehicle.licensePlate || text.noPlate]}
                changeLabel={text.change}
                onChange={() => { setSelectedVehicle(undefined); setValue("vehicleId", ""); }}
              />
              <MileageField control={control} />
            </> : <>
              {customerId && (customerVehicles.data?.items.length ?? 0) > 0 && <Form.Item label={text.existingVehicles}><Select
                placeholder={text.selectVehicle}
                options={customerVehicles.data!.items.map(vehicle => ({ value: vehicle.id, label: `${vehicle.year} ${vehicle.make} ${vehicle.model} · ${vehicle.vin}` }))}
                onChange={id => {
                  const vehicle = customerVehicles.data!.items.find(item => item.id === id);
                  if (vehicle) selectVehicle(vehicle);
                }}
              /></Form.Item>}
              <Row gutter={12}>
                <Col xs={24}><Controller name="vin" control={control} render={({ field }) => <Form.Item label={text.vin} validateStatus={errors.vin ? "error" : undefined} help={errors.vin?.message}><Input
                  {...field}
                  className="mono"
                  maxLength={17}
                  value={normalizeVinInput(field.value ?? "")}
                  onChange={event => field.onChange(normalizeVinInput(event.target.value))}
                  suffix={<FieldScanButton mode="vin" onResult={field.onChange} />}
                /></Form.Item>} /></Col>
                <Col xs={8}><Controller name="year" control={control} render={({ field }) => <Form.Item label={text.year} validateStatus={errors.year ? "error" : undefined} help={errors.year?.message}><InputNumber {...field} precision={0} style={{ width: "100%" }} /></Form.Item>} /></Col>
                <Col xs={8}><FastTextField control={control} name="make" label={text.make} error={errors.make?.message} /></Col>
                <Col xs={8}><FastTextField control={control} name="model" label={text.model} error={errors.model?.message} /></Col>
                <Col xs={24} md={12}><FastTextField control={control} name="licensePlate" label={text.plate} uppercase scanMode="plate" /></Col>
                <Col xs={24} md={12}><MileageField control={control} /></Col>
              </Row>
              {vinDecode.isFetching && <Alert className="vin-decode-note" type="info" showIcon message={text.decodingVin} />}
              {vinDecode.data && <Alert
                className="vin-decode-note"
                type={vinDecode.data.year || vinDecode.data.make || vinDecode.data.model ? "success" : "warning"}
                showIcon
                message={vinDecode.data.year || vinDecode.data.make || vinDecode.data.model ? text.vinDecoded : text.vinDecodePartial}
              />}
              {vinDecode.isError && <Alert className="vin-decode-note" type="warning" showIcon message={text.vinDecodeUnavailable} />}
            </>}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title={text.serviceRequest}>
            <Controller name="customerConcern" control={control} render={({ field }) => <Form.Item label={text.customerConcern} validateStatus={errors.customerConcern ? "error" : undefined} help={errors.customerConcern?.message}><Input.TextArea {...field} rows={3} autoFocus={Boolean(effectiveVehicle)} placeholder={text.complaintPlaceholder} /></Form.Item>} />
            <Controller name="promisedAt" control={control} render={({ field }) => <Form.Item label={text.promisedAt}><DatePicker showTime={{ format: "HH:mm" }} format="MM/DD/YYYY HH:mm" value={field.value ? dayjs(field.value) : null} onChange={value => field.onChange(value?.toISOString())} style={{ width: "100%" }} /></Form.Item>} />
          </Card>
        </Col>
      </Row>

      <div className="fast-ro-action-bar">
        <div><span>{savedAt ? `${text.draftSavedAt} ${dayjs(savedAt).format("HH:mm:ss")}` : text.manualSaveHint}</span></div>
        <Space>
          <Button icon={<SaveOutlined />} onClick={saveDraft}>{text.saveDraft}</Button>
          <Button onClick={() => navigate("/")}>{text.cancel}</Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            icon={<PlusOutlined />}
            loading={create.isPending}
            disabled={Boolean(existingActiveEvent)}
          >{text.createRepairEvent}</Button>
        </Space>
      </div>
    </form>
  </section>;
}

function FastTextField({ control, name, label, error, nameCase, uppercase, scanMode }: {
  control: Control<FastRoValues>;
  name: keyof FastRoValues;
  label: string;
  error?: string;
  nameCase?: boolean;
  uppercase?: boolean;
  scanMode?: "vin" | "plate";
}) {
  return <Controller name={name} control={control} render={({ field }) => <Form.Item label={label} validateStatus={error ? "error" : undefined} help={error}><Input
    {...field}
    value={String(field.value ?? "")}
    onChange={event => field.onChange(nameCase
      ? capitalizeNameInput(event.target.value)
      : uppercase ? event.target.value.toUpperCase() : event.target.value)}
    suffix={scanMode ? <FieldScanButton mode={scanMode} onResult={field.onChange} /> : undefined}
  /></Form.Item>} />;
}

function MileageField({ control }: { control: Control<FastRoValues> }) {
  const { text } = useAppConfig();
  return <Controller name="mileageIn" control={control} render={({ field }) => <Form.Item label={text.mileage}><InputNumber {...field} min={0} precision={0} formatter={formatGroupedInput} parser={parseGroupedInput} style={{ width: "100%" }} /></Form.Item>} />;
}

function SelectedEntity({ title, lines, changeLabel, onChange }: {
  title: string;
  lines: string[];
  changeLabel: string;
  onChange: () => void;
}) {
  return <div className="fast-ro-selected"><div><Typography.Title level={4}>{title}</Typography.Title>{lines.map(line => <span key={line}>{line}</span>)}</div><Button type="link" onClick={onChange}>{changeLabel}</Button></div>;
}

function loadFastRoDraft(): { values: Partial<FastRoValues>; savedAt: string } | undefined {
  try {
    const raw = localStorage.getItem(FAST_RO_DRAFT_KEY);
    if (!raw) return undefined;
    const draft = JSON.parse(raw) as { values?: Partial<FastRoValues>; savedAt?: string };
    return draft.values && draft.savedAt ? { values: draft.values, savedAt: draft.savedAt } : undefined;
  } catch {
    localStorage.removeItem(FAST_RO_DRAFT_KEY);
    return undefined;
  }
}

function inferLookup(value: string): Partial<FastRoValues> {
  if (!value) return {};
  const vin = normalizeVinInput(value);
  if (/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) return { vin };
  const phoneDigits = value.replace(/\D/g, "");
  if (phoneDigits.length >= 7 && phoneDigits.length <= 11) return { phone: formatPhoneInput(phoneDigits) };
  if (!value.includes(" ") && /^[A-Za-z0-9-]{2,10}$/.test(value) && /\d/.test(value)) {
    return { licensePlate: value.toUpperCase() };
  }
  const names = value.trim().split(/\s+/);
  if (/^[A-Za-zÀ-ÖØ-öø-ÿ'’-]+(?:\s+[A-Za-zÀ-ÖØ-öø-ÿ'’-]+)*$/.test(value.trim())) {
    return {
      firstName: capitalizeNameInput(names[0] ?? ""),
      lastName: capitalizeNameInput(names.slice(1).join(" "))
    };
  }
  return {};
}

function vehicleDisplayCase(value: string) {
  if (value.length <= 3) return value;
  return value === value.toUpperCase()
    ? value.toLowerCase().replace(/\b\w/g, character => character.toUpperCase())
    : value;
}
